/**
 * useThemePref — per-device light/dark override.
 *
 * The kitchen tablet's dusk theme is driven automatically by DuskTheme
 * (admin `theme-mode` override + Met.no sun times).  This store layers a
 * *per-device* manual override on top so anyone can force light or dark
 * from Settings without touching the household-wide admin config:
 *
 *   - `'auto'`  → defer to DuskTheme's admin + sun rule (default)
 *   - `'light'` → force `html.theme-dusk` OFF on this device
 *   - `'dark'`  → force `html.theme-dusk` ON on this device
 *
 * Persisted to localStorage (key `stell.theme-pref`) so the choice
 * survives reloads.  It is deliberately device-local — a phone in dark
 * mode shouldn't flip the kitchen tablet.  DuskTheme reads `pref` and
 * gives it priority over the auto/admin branches.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePref = 'auto' | 'light' | 'dark';

interface ThemePrefState {
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
}

export const useThemePref = create<ThemePrefState>()(
  persist(
    (set) => ({
      pref: 'auto',
      setPref: (pref) => set({ pref }),
    }),
    { name: 'stell.theme-pref' },
  ),
);
